import { useCallback, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Settings, User, Volume2, Target, Save, Pencil, X, Check, Trash2 } from "lucide-react";
import { API, useAuth, authFetch } from "@/App";
import { RECITERS } from "@/data/reciters";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [settings, setSettings] = useState({
    daily_goal: 5,
    memorization_settings: {
      loop_count: 3,
      pause_duration: 2,
      reciter: "ar.alafasy",
      playback_speed: 1
    }
  });

  const reciters = RECITERS;

  const translations = [
    { value: "en.sahih", label: "Sahih International (English)" },
    { value: "en.pickthall", label: "Pickthall (English)" },
    { value: "en.yusufali", label: "Yusuf Ali (English)" },
    { value: "en.asad", label: "Muhammad Asad (English)" },
    { value: "ur.jalandhry", label: "Jalandhry (Urdu)" },
    { value: "fr.hamidullah", label: "Hamidullah (French)" },
    { value: "id.indonesian", label: "Indonesian" },
    { value: "tr.diyanet", label: "Diyanet (Turkish)" }
  ];

  const saveProfileName = async () => {
    const trimmed = profileName.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      const res = await authFetch(`${API}/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      if (res.ok) {
        const data = await res.json();
        updateUser({ name: data.name });
        toast.success('Profile updated');
        setEditingName(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch (e) {
      toast.error('Failed to update profile');
    } finally {
      setSavingName(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/settings`);
      if (res.ok) {
        const data = await res.json();
        setSettings({
          daily_goal: data.daily_goal || 5,
          memorization_settings: {
            loop_count: data.memorization_settings?.loop_count || 3,
            pause_duration: data.memorization_settings?.pause_duration || 2,
            reciter: data.memorization_settings?.reciter || "ar.alafasy",
            playback_speed: data.memorization_settings?.playback_speed || 1,
            translation: data.memorization_settings?.translation || "en.sahih"
          }
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (user?.name) setProfileName(user.name);
  }, [user?.name]);

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const res = await authFetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Settings saved!');
      } else {
        // Surface the real reason so we can diagnose any save failure.
        let detail = `HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data?.detail) {
            detail = typeof data.detail === 'string'
              ? data.detail
              : JSON.stringify(data.detail);
          }
        } catch (parseErr) { console.error("settings save parse:", parseErr); }
        throw new Error(detail);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(`Failed to save settings: ${error.message || 'Network error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const updateMemorizationSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      memorization_settings: {
        ...prev.memorization_settings,
        [key]: value
      }
    }));
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm');
      return;
    }
    setDeletingAccount(true);
    try {
      const res = await authFetch(`${API}/auth/me`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      toast.success("Account deleted. Goodbye.");
      // Local cleanup + hard navigate — logout() also clears quran_user.
      try {
        await logout?.();
      } catch (logoutErr) { console.error("logout failed:", logoutErr); }
      localStorage.removeItem("quran_user");
      navigate("/");
    } catch (err) {
      toast.error(`Could not delete account: ${err.message || "Network error"}`);
    } finally {
      setDeletingAccount(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="min-h-screen bg-islamic-blue-950">
      {/* Header */}
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100">Settings</h1>
          </div>

          <Button
            data-testid="save-settings-btn"
            onClick={saveSettings}
            disabled={isSaving}
            className="btn-gold rounded-full px-4 py-2 text-sm"
          >
            {isSaving ? (
              <div className="spinner w-4 h-4"></div>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        {user?.is_guest && <GuestUpgradeSection user={user} updateUser={updateUser} />}

        {/* Profile Section */}
        <section className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gold-400/10 flex items-center justify-center">
              <User className="w-5 h-5 text-gold-400" />
            </div>
            <h2 className="font-cinzel text-lg font-semibold text-slate-100">Profile</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 font-outfit">Name</Label>
              {editingName ? (
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="profile-name-input"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    maxLength={100}
                    autoFocus
                    className="bg-islamic-blue-950/50 border-white/10 text-slate-200"
                  />
                  <Button
                    data-testid="save-profile-name-btn"
                    onClick={saveProfileName}
                    disabled={savingName}
                    className="btn-gold shrink-0"
                  >
                    {savingName ? <div className="spinner w-4 h-4" /> : <><Check className="w-4 h-4 mr-1"/>Save</>}
                  </Button>
                  <Button
                    data-testid="cancel-profile-name-btn"
                    variant="ghost"
                    onClick={() => { setEditingName(false); setProfileName(user?.name || ""); }}
                    className="shrink-0 text-slate-400 hover:text-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="profile-name"
                    value={user?.name || ''}
                    disabled
                    className="bg-islamic-blue-950/50 border-white/10 text-slate-300"
                  />
                  <Button
                    data-testid="edit-profile-name-btn"
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingName(true)}
                    className="shrink-0 text-slate-400 hover:text-gold-400 hover:bg-gold-400/10"
                    title="Edit name"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-slate-300 font-outfit">Email</Label>
              <Input
                data-testid="profile-email"
                value={user?.email || ''}
                disabled
                className="mt-2 bg-islamic-blue-950/50 border-white/10 text-slate-300"
              />
            </div>
          </div>
        </section>

        {/* Daily Goal Section */}
        <section className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-500" />
            </div>
            <h2 className="font-cinzel text-lg font-semibold text-slate-100">Daily Goal</h2>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-slate-300 font-outfit">Ayahs per day</Label>
              <span className="text-gold-400 font-outfit font-medium">{settings.daily_goal}</span>
            </div>
            <Slider
              data-testid="daily-goal-slider"
              value={[settings.daily_goal]}
              onValueChange={([val]) => setSettings(prev => ({ ...prev, daily_goal: val }))}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
            <p className="text-slate-500 text-sm font-outfit mt-2">
              Set a realistic daily goal to maintain consistency
            </p>
          </div>
        </section>

        {/* Audio Settings Section */}
        <section className="glass-card rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Volume2 className="w-5 h-5 text-purple-500" />
            </div>
            <h2 className="font-cinzel text-lg font-semibold text-slate-100">Audio Settings</h2>
          </div>

          <div className="space-y-6">
            {/* Reciter */}
            <div>
              <Label className="text-slate-300 font-outfit mb-2 block">Reciter</Label>
              <Select
                value={settings.memorization_settings.reciter}
                onValueChange={(val) => updateMemorizationSetting('reciter', val)}
              >
                <SelectTrigger 
                  data-testid="reciter-select"
                  className="bg-islamic-blue-950/50 border-white/10 text-slate-200"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-islamic-blue-900 border-white/10">
                  {reciters.map(r => (
                    <SelectItem 
                      key={r.value} 
                      value={r.value}
                      className="text-slate-200 focus:bg-gold-400/10 focus:text-gold-400"
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Translation */}
            <div>
              <Label className="text-slate-300 font-outfit mb-2 block">Translation</Label>
              <Select
                value={settings.memorization_settings.translation || "en.sahih"}
                onValueChange={(val) => updateMemorizationSetting('translation', val)}
              >
                <SelectTrigger
                  data-testid="translation-select"
                  className="bg-islamic-blue-950/50 border-white/10 text-slate-200"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-72">
                  {translations.map(t => (
                    <SelectItem
                      key={t.value}
                      value={t.value}
                      className="text-slate-200 focus:bg-gold-400/10 focus:text-gold-400"
                    >
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-slate-500 text-xs font-outfit mt-2">
                Choose your preferred translation displayed in the Reader
              </p>
            </div>

            {/* Loop Count */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-300 font-outfit">Loop Count</Label>
                <span className="text-gold-400 font-outfit font-medium">
                  {settings.memorization_settings.loop_count}x
                </span>
              </div>
              <Slider
                data-testid="loop-count-slider"
                value={[settings.memorization_settings.loop_count]}
                onValueChange={([val]) => updateMemorizationSetting('loop_count', val)}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
              <p className="text-slate-500 text-sm font-outfit mt-2">
                How many times to repeat each ayah during memorization
              </p>
            </div>

            {/* Pause Duration */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-300 font-outfit">Pause Between Ayahs</Label>
                <span className="text-gold-400 font-outfit font-medium">
                  {settings.memorization_settings.pause_duration}s
                </span>
              </div>
              <Slider
                data-testid="pause-duration-slider"
                value={[settings.memorization_settings.pause_duration]}
                onValueChange={([val]) => updateMemorizationSetting('pause_duration', val)}
                min={0}
                max={10}
                step={0.5}
                className="w-full"
              />
              <p className="text-slate-500 text-sm font-outfit mt-2">
                Time to pause between verses for recitation from memory
              </p>
            </div>

            {/* Playback Speed */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-slate-300 font-outfit">Playback Speed</Label>
                <span className="text-gold-400 font-outfit font-medium">
                  {settings.memorization_settings.playback_speed}x
                </span>
              </div>
              <Slider
                data-testid="playback-speed-slider"
                value={[settings.memorization_settings.playback_speed]}
                onValueChange={([val]) => updateMemorizationSetting('playback_speed', val)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
              />
              <p className="text-slate-500 text-sm font-outfit mt-2">
                Adjust audio speed for learning (slower can help with pronunciation)
              </p>
            </div>
          </div>
        </section>

        {/* Parent / Teacher */}
        <section
          data-testid="settings-teacher-section"
          className="glass-card rounded-xl p-6"
        >
          <h2 className="font-cinzel text-lg font-semibold text-slate-100 mb-2">
            Parent / Teacher
          </h2>
          <p className="text-slate-400 text-sm font-outfit mb-3">
            Add students or children and assign them Quran lessons with notes.
          </p>
          <Link
            data-testid="settings-teacher-link"
            to="/teacher"
            className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-outfit"
          >
            Open Teacher Dashboard →
          </Link>
        </section>

        {/* Contact / Support */}
        <section
          data-testid="contact-section"
          className="glass-card rounded-xl p-6"
        >
          <h2 className="font-cinzel text-lg font-semibold text-slate-100 mb-2">Contact</h2>
          <p className="text-slate-400 text-sm font-outfit mb-3">
            Feedback, feature requests, or bug reports — write to us:
          </p>
          <a
            href="mailto:myhifz@hifz.me"
            data-testid="contact-email-link"
            className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-outfit"
          >
            myhifz@hifz.me
          </a>
        </section>

        {/* Danger Zone — Delete account */}
        <section
          data-testid="danger-zone-section"
          className="glass-card rounded-xl p-6 border border-red-500/20"
        >
          <h2 className="font-cinzel text-lg font-semibold text-red-400 mb-2 flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete Account
          </h2>
          <p className="text-slate-400 text-sm font-outfit mb-4">
            Permanently delete your account, progress, bookmarks, lessons, and
            all associated data. This cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <Button
              data-testid="delete-account-btn"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete my account
            </Button>
          ) : (
            <div className="space-y-3 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
              <p className="text-slate-200 text-sm font-outfit">
                Type <span className="font-mono font-bold text-red-300">DELETE</span> to confirm.
              </p>
              <Input
                data-testid="delete-confirm-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="bg-islamic-blue-900 border-red-500/30 text-slate-100"
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  data-testid="cancel-delete-btn"
                  variant="ghost"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                  className="text-slate-300 hover:text-slate-100"
                  disabled={deletingAccount}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-delete-account-btn"
                  onClick={handleDeleteAccount}
                  disabled={
                    deletingAccount ||
                    deleteConfirmText.trim().toUpperCase() !== "DELETE"
                  }
                  className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                >
                  {deletingAccount ? "Deleting..." : "Permanently delete"}
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* App Info */}
        <section className="text-center py-6">
          <p className="text-slate-600 text-sm font-outfit">Hifz · Quran Memorization App v1.0</p>
          <p className="text-slate-700 text-xs font-outfit mt-1">Built with reverence for the sacred text</p>
        </section>
      </main>
    </div>
  );
}

function GuestUpgradeSection({ user, updateUser }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${API}/auth/convert-guest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Couldn't upgrade account");
      updateUser({ email: data.email, name: data.name, is_guest: false });
      toast.success("Account upgraded! You can now sign in with your email.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="guest-upgrade-section"
      className="rounded-xl p-6 border border-gold-400/30 bg-gold-400/5"
    >
      <h2 className="font-cinzel text-lg text-slate-100 mb-1">Upgrade your guest account</h2>
      <p className="text-slate-400 text-sm font-outfit mb-5">
        Keep every badge, bookmark and streak — just add an email and password.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-slate-300 font-outfit">Display name</Label>
          <Input
            data-testid="upgrade-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="mt-1 bg-islamic-blue-950/50 border-white/10 text-slate-100"
          />
        </div>
        <div>
          <Label className="text-slate-300 font-outfit">Email</Label>
          <Input
            data-testid="upgrade-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 bg-islamic-blue-950/50 border-white/10 text-slate-100"
          />
        </div>
        <div>
          <Label className="text-slate-300 font-outfit">Password</Label>
          <Input
            data-testid="upgrade-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            required
            className="mt-1 bg-islamic-blue-950/50 border-white/10 text-slate-100"
          />
        </div>
        <Button
          type="submit"
          disabled={saving}
          data-testid="submit-upgrade-btn"
          className="btn-gold rounded-full px-6"
        >
          {saving ? "Upgrading…" : "Upgrade account"}
        </Button>
      </form>
    </section>
  );
}
